<template>
    <div
        v-if="mainLoaded"
        id="app"
    >
        <div aria-live="polite">
            <span class="sr-only"> {{ loadSpinnerText }}</span>
        </div>
        <div
            v-if="isLoading"
            id="loading-spinner"
        >
            <clip-loader
                :loading="isLoading"
                :color="color"
                :size="size"
            />
        </div>

        <notifications
            group="infoNotification"
            position="top center"
            :max="2"
            :ignore-duplicates="true"
            :close-on-click="false"
        >
            <template #body="props">
                <div
                    role="alert"
                    class="visa-notify"
                    :class="props.item.type"
                >
                    <div class="custom-template-content">
                        <div class="custom-template-title">
                            {{ props.item.title }}
                        </div>
                        <div class="custom-template-text">
                            {{ props.item.text }}
                        </div>
                    </div>
                    <div
                        class="custom-template-close"
                        @click="props.close"
                    >
                        <span class="close-mark">
                            X
                        </span>
                    </div>
                </div>
            </template>
        </notifications>

        <router-view />

        <modal v-show="isModalVisible" />
    </div>
</template>

<script lang="ts">
    import { Component, Vue, Watch } from "vue-facing-decorator"
    import Modal from "@/layout/Modal.vue"
    import ClipLoader from "vue-spinner/src/ClipLoader.vue"
    import utils from "@/utils/utils"
    import ApiService from "@/services/ApiService"
    import OperatorModel from "@/models/operatormodel"

    const DISPLAY_TYPES = {
        XL1920: 1920,
        L1680: 1680,
        M1100: 1100,
        S768: 768
    }

    // Raja jonka jälkeen mobiilifooterissa vain yksi palsta teksteille
    const MINWIDTH_FOR_TWOCOLUMN_MOBILEFOOTER = 1449

    @Component({
        components: {
            Modal,
            clipLoader: ClipLoader
        }
    })
    export default class App extends Vue {
        resizePageWidth: number = 0
        color = "#000000"
        size = "100px"

        loadSpinnerText: string = ""

        async created(): Promise<void> {
            await this.initApp()

            window.addEventListener("resize", this.resize)
            // Set the right display type
            this.resize()
        }

        mounted(): void {
            setTimeout(() => {
                this.$store.dispatch("setLoading", { name: "main", value: false })
            }, 5000)
        }

        async initApp() {
            this.$store.dispatch("setLoading", { name: "main", value: true })
            const baseHref = "/" + utils.getCurrentSubdomain()
            this.$store.dispatch("setBaseHref", baseHref)
            const apiService = new ApiService(baseHref)
            const infokarttaApiCallBasePart = "v1/Infokartta/"
            const RadiomikrofonitApiCallBasePart: string = "v1/Radiomikrofonitaajuudet/"

            apiService.getConfigValues().then(async (response) => {
                const now = new Date(Date.now())
                const timeStamp = now.getFullYear()
                    + (now.getMonth() + 1).toString().padStart(2, "0")
                    + now.getDate().toString().padStart(2, "0")
                    + now.getHours().toString().padStart(2, "0")

                await this.$store.dispatch("setContentServiceUrl", response.contentServiceUrl)
                await this.$store.dispatch("setContentServiceTimeStamp", timeStamp)
                await this.$store.dispatch("setLang", response.langId)
                await this.$store.dispatch("setMapServicesUrl", response.mapServicesUrl)
                await this.$store.dispatch("setDataPublishServicesUrl", response.dataPublishServicesUrl)
                await this.$store.dispatch("setLocationSearchMinZoom", Number(response.locationSearchMinZoom))
                await this.$store.dispatch("setRadiomikrofonitMinFrequency", response.radiomikrofonitMinFrequency)
                await this.$store.dispatch("setRadiomikrofonitMaxFrequency", response.radiomikrofonitMaxFrequency)

                await apiService.getPageWhitelist().then(async (response) => {
                    const map = await new Map(Object.entries(JSON.parse(response)))
                    await this.$store.dispatch("setPageWhitelist", map)
                })

                await apiService.getLegendLimits().then(async (response) => {
                    const map = new Map(Object.entries(JSON.parse(response)))
                    await this.$store.dispatch("setLegendLimits", map)
                })

                await apiService.getBittimittariOperators().then(async (response) => {
                    const operatorList: OperatorModel[] = []
                    for (const operator of response) {
                        const operatorItem = new OperatorModel(operator.id, operator.nimi, false)
                        operatorList.push(operatorItem)
                    }

                    // Sortataan operaattorit aakkosjärjestykseen
                    operatorList.sort((a, b) => a.name.localeCompare(b.name, 'fi'))

                    await this.$store.dispatch("setOperatorList", operatorList)
                })

                // Ladataan infokartat sivukarttaa varten
                await apiService.dynamicApiCall(infokarttaApiCallBasePart + "Infokartat?infokartanTyyppi=Liikenne").then(async (response) => {
                    await this.$store.dispatch("setLiikenneInfokarttaRadioValues", response)
                })

                await apiService.dynamicApiCall(infokarttaApiCallBasePart + "Infokartat?infokartanTyyppi=LaajakaistaJaPuhelin").then(async (response) => {
                    await this.$store.dispatch("setLaajakaistaJaPuhelinInfokarttaRadioValues", response)
                })

                await apiService.dynamicApiCall(infokarttaApiCallBasePart + "Infokartat?infokartanTyyppi=Posti").then(async (response) => {
                    await this.$store.dispatch("setPostiInfokarttaRadioValues", response)
                })

                await apiService.dynamicApiCall(RadiomikrofonitApiCallBasePart + "Radiomikrofonitaajuudet").then(async (response) => {
                    // Haetaan radiomikeistä oletus-ylä- ja alaraja sekä kaistanleveys
                    await this.$store.dispatch("setMinMaxRadioMFrequencies", { min: response.minfrequency, max: response.maxfrequency })
                    await this.$store.dispatch("setRadiomikrofonitBandwidth", response.bandwidth)
                })
            }).finally(() => {
                // päivitetään sivun title
                let currentPageName = ""
                if (this.$route.params.pageName !== undefined) {
                    currentPageName = this.$route.params.pageName as string
                }
                utils.changePageTitle(undefined, currentPageName)
                // haetaan kunnat ja maakunnat geometrioineen muistiin
                apiService.dynamicApiCall("v1/AreaInfoService/Kunnat?getGeoms=true").then(response => {
                    this.$store.dispatch("setMunicipalitiesData", response)
                }).finally(() => {
                    apiService.dynamicApiCall("v1/AreaInfoService/Maakunnat?getGeoms=true").then(response => {
                        this.$store.dispatch("setRegionsData", response)
                    }).finally(() => {
                        this.$store.dispatch("setMainLoaded", true)
                        // this.$store.dispatch("setLoading", { name: "main", value: false })
                    })
                })
            })
        }

        get isLoading(): boolean {
            return this.$store.getters.isLoading
        }

        get isModalVisible(): boolean {
            return this.$store.getters.modalComponentName !== ""
        }

        get currentDisplay(): string {
            return this.$store.getters.displayType
        }

        get mainLoaded(): boolean {
            return this.$store.getters.mainLoaded
        }

        get loadSpinnerLoadingText(): string {
            return this.$t("App.LoadSpinnerText.Loading") ?? ""
        }

        get loadSpinnerCompleteText(): string {
            return this.$t("App.LoadSpinnerText.Complete") ?? ""
        }

        get displayLarge(): string {
            return this.$store.state.displayLarge
        }

        get displayExtraLarge(): string {
            return this.$store.state.displayExtraLarge
        }

        get displayMedium(): string {
            return this.$store.state.displayMedium
        }

        get displaySmall(): string {
            return this.$store.state.displaySmall
        }

        get displayExtraSmall(): string {
            return this.$store.state.displayExtraSmall
        }

        resize(): void {
            // Kun leveys muuttuu, tarkistetaan vaihtuuko display type. Jos vaihtuu, tallennetaan muutos storeen
            const pageWidth = window.innerWidth
            if (pageWidth >= DISPLAY_TYPES.XL1920 && this.currentDisplay !== this.displayExtraLarge) {
                this.changeDisplayType(this.displayExtraLarge)
            }
            else if (pageWidth >= DISPLAY_TYPES.L1680 && pageWidth < DISPLAY_TYPES.XL1920 && this.currentDisplay !== this.displayLarge) {
                this.changeDisplayType(this.displayLarge)
            }
            else if (pageWidth >= DISPLAY_TYPES.M1100 && pageWidth < DISPLAY_TYPES.L1680 && this.currentDisplay !== this.displayMedium) {
                this.changeDisplayType(this.displayMedium)
            }
            else if (pageWidth >= DISPLAY_TYPES.S768 && pageWidth < DISPLAY_TYPES.M1100 && this.currentDisplay !== this.displaySmall) {
                this.changeDisplayType(this.displaySmall)
            }
            else if (pageWidth < DISPLAY_TYPES.S768 && this.currentDisplay !== this.displayExtraSmall) {
                this.changeDisplayType(this.displayExtraSmall)
            }
            else {
                // TODO OKo: error? Tänne ei pitäisi koskaan päätyä?
            }

            // Tarkistetaan ja päivitetään store-muuttuja vain jos sen arvo muuttuu
            this.$store.dispatch("setTwoColumnsMobileFooter", pageWidth > MINWIDTH_FOR_TWOCOLUMN_MOBILEFOOTER)
        }

        changeDisplayType(displayType: string): void {
            /* Display types:
            * xl = 1920+
            * l = 1919 - 1680
            * m = 1679 - 1100
            * s = 1099 - 768
            * xs = 767 - 0
            */
            this.$store.dispatch("setDisplayType", displayType)
        }

        /* modal-open -luokka estää sivun scrollin taustalla, jos modaali on auki */
        @Watch("isModalVisible")
        handlePageScroll() {
            if (!this.isModalVisible) {
                document.body.classList.remove("modal-open")
            }
            else {
                document.body.classList.add("modal-open")
            }
        }

        @Watch("isLoading")
        handleLoadSpinnerText() {
            if (this.isLoading) {
                this.loadSpinnerText = this.loadSpinnerLoadingText
            }
            else {
                this.loadSpinnerText = this.loadSpinnerCompleteText
                setTimeout(() => {
                    this.loadSpinnerText = ""
                }, 5000)
            }
        }

        destroyed(): void {
            window.removeEventListener("resize", this.resize)
        }
    }
</script>

<style>
    body.modal-open {
        overflow: hidden;
    }

    #app input.default {
        height: 3.125rem;
        border-width: 0.0625rem;
    }

    .search-components {
        padding-top: 2rem;
    }

    #app textarea.custom-search::placeholder {
        position: absolute;
        line-height: 1;
        display: block;
        top: 50%;
        transform: translateY(-50%);
    }

    #app input.search {
        border-right: 1px solid var(--Traficom-main-color);
        padding-right: 0px;
    }

    div.search-address-focus {
        float: right;
        margin: 2rem 2rem 0rem 0.5rem;
        padding-bottom: 1rem;
    }

        div.search-address-focus a {
            cursor: pointer;
        }

    #app .dropdown > select {
        height: 3.125rem;
    }

    /* --- vue-notification custom tyylit --- */
    .vue-notification-group {
        position: absolute !important;
        width: 400px !important;
        left: calc(50% - 200px) !important;
        z-index: 1040;
    }

    /* perustyylit */
    .visa-notify {
        background-color: white !important;
        font-size: 0.875rem !important;
        border: 2px solid lightgreen !important;
        color: black !important;
        font-weight: 600;
        text-align: center !important;
        display: flex;
        padding: 13px;
        margin-bottom: 5px;
        position: absolute;
        top: 5rem;
    }

        .visa-notify.error {
            border: 2px solid red !important;
        }

        .visa-notify.warn {
            border: 2px solid orange !important;
        }

        .visa-notify .custom-template-close {
            align-self: center;
            margin-left: 1px;
            cursor: pointer;
            flex-grow: 1;
            text-align: right;
        }

            .visa-notify .custom-template-close .close-mark {
                border: 1px solid darkgray;
                padding: 0px 3px;
                font-family: cursive;
            }

    #loading-spinner {
        z-index: 10000;
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        top: 0;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        background: #fff;
        opacity: 0.5;
    }

        #loading-spinner > div {
            margin: 0;
            top: 50%;
            -ms-transform: translateY(-50%);
            transform: translateY(-50%);
            position: relative;
        }

    /* Global styles */
    #app {
        --var-slot-width-xl-l: 500px;
        word-break: normal;
        height: 100%;
        background-color: #F2F4F8;
    }

        #app .left-slot p {
            white-space: pre-line;
            text-align: left;
        }

        #app .left-slot button {
            white-space: pre-line;
            text-align: left;
        }

        #app .left-slot fieldset.fieldset {
            padding-top: 1rem;
            padding-bottom: 1rem;
        }

    .iframe-external-icon {
        width: 0.875rem;
        margin-left: 5px;
    }

    section.popup-item {
        margin: 20px 0 10px 0;
    }

    section.popup-item p {
        margin: 0;
    }

    .content-text-info {
        margin-top: 20px;
    }

    .star-rating {
        margin-top: 20px;
    }

    .modal-item-info {
        font-weight: bold;
    }

    .modal-item-info > span {
        font-weight: 400;
    }

    .modal-header h1:focus {
        outline: 2px solid #000;
    }

    a.open-modal-link {
        color: var(--Traficom-main-color);
        background-image: none;
        font-weight: 600;
        cursor: pointer;
    }

        a.open-modal-link:hover {
            background-image: linear-gradient(var(--Traficom-main-color) 50%, #FFFFFF 50%);
        }

    .leaflet-touch div.leaflet-bar a {
        height: 1.875rem;
        width: 1.875rem;
    }

    .sr-only {
        position: absolute !important;
        height: 1px;
        width: 1px;
        margin: -1px;
        padding: 0;
        overflow: hidden;
        clip: rect(1px 1px 1px 1px); /* IE6, IE7 */
        clip: rect(1px, 1px, 1px, 1px);
        white-space: nowrap; /* keep from collapsing into non-existant height*/
    }

    .close-icon {
        width: 1rem;
        height: 1rem;
    }

    .visually-hidden {
        position: fixed !important;
    }
    /*
        Fonts // TODO OKo: Lisää leiskoista löytyvä nimi kommenttina
    */
    @media (min-width: 768px) {
        .app-name-label {
            font: normal 1.5rem Montserrat;
            color: var(--Traficom-main-color);
        }

        /* H1-sisältö */
        #app h1 {
            text-align: left;
            font-family: Montserrat;
            font-size: 1.5rem;
        }

        #app h2 {
            font-family: Montserrat;
            font-size: 1.125rem;
        }

        #app h3 {
            font-family: Montserrat;
            font-size: 1.125rem;
            font-weight: 600; /* semi bold */
        }

            #app h3.h1 {
                font-family: Montserrat;
                font-size: 1.5rem;
                font-weight: 300;
            }

        #app .address-font {
            font-family: Montserrat;
            font-size: 1rem;
        }

        #app h2 span.filtering {
            font-family: Montserrat;
            font-size: 1.5rem;
            margin-bottom: 0px;
        }

        #app h2 span.filtering-subtext {
            margin-top: 0px;
            margin-bottom: 0px;
        }

        #app h2 span.filtering-subtext {
            font-family: Montserrat;
            font-size: 1.125rem;
            font-weight: 600; /* semi bold */
        }

        .subject-selection-font {
            font-size: 1rem;
            font-weight: 600;
        }

        .table-font {
            font-size: 1rem;
        }

        .modal-font {
            font-size: 1rem;
        }

        .navigation-font {
            font-family: Montserrat;
            font-size: 1rem;
        }

        div.leaflet-bar a.leaflet-disabled {
            color: #8A8A8A;
        }
    }

    @media (max-width: 767px) {
        .app-name-label {
            font: normal 1.25rem Montserrat;
            color: var(--Traficom-main-color);
        }

        #app h1 {
            font-family: Montserrat;
            font-size: 1.25rem;
            word-break: break-word;
        }

        #app h2 {
            font-family: Montserrat;
            font-size: 1rem;
        }

        #app h3, #app h2 span.filtering-subtext {
            font-family: Montserrat;
            font-size: 1.125rem;
            font-weight: 600; /* semi bold */
        }

            #app h3.h1 {
                font-family: Montserrat;
                font-size: 1.25rem;
                font-weight: 300;
            }

        #app .address-font {
            font-family: Montserrat;
            font-size: 0.875rem;
        }

        #app h2 span.filtering {
            font-family: Montserrat;
            font-size: 1rem;
            margin-bottom: 0px;
        }

        #app h2 span.filtering {
            font-family: Montserrat;
            font-size: 1.125rem;
            font-weight: 600; /* semi bold */
        }

        #app .subject-selection-font {
            font-size: 1rem;
            font-weight: 600;
        }

        #app .table-font {
            font-size: 0.875rem;
        }

        #app .modal-font {
            font-size: 0.875rem;
        }

        #app .navigation-font {
            font-size: 1rem;
        }

        #app button.primary {
            font-size: 1.125rem;
        }
    }

    @media (max-width: 500px) {
        /* --- vue-notification custom tyylit --- */
        .vue-notification-group {
            width: 300px !important;
            left: calc(50% - 150px) !important;
        }
    }

    @media (max-width: 400px) {
        /* --- vue-notification custom tyylit --- */
        .vue-notification-group {
            width: 220px !important;
            left: calc(50% - 110px) !important;
        }
    }

    /* Left slot*/
    #app .left-container h1 {
        margin-bottom: 2rem;
        word-wrap: break-word;
    }

    #app .left-container h2,
    #app .left-container h3 {
        font-weight: 500;
        margin-bottom: 1.2rem;
    }

    #app .left-slot textarea:focus,
    #app .left-slot input:focus,
    #app .left-slot select:focus,
    #app .left-slot a:focus,
    #app .left-slot button:focus,
    #app .left-slot button:focus,
    .modal-header h1:focus,
    .modal-header .btn-close:focus {
        outline: 3px solid var(--Traficom-main-color);
        outline-offset: 2px;
    }

    #app button.btn-close:focus {
        box-shadow: none;
    }
</style>
